import {
  createCenterAgentApi,
  createOrganizationApi,
  type StructuredApiClientFailure,
  toStructuredApiClientFailure,
} from "@wakeloop/api-client";
import type {
  CenterAgentDesignation,
  DesignateCenterAgentResponse,
  GetCenterAgentDesignationResponse,
  ListCenterAgentAuditJournalResponse,
  OrganizationDirectoryAgentProfile,
  RevokeCenterAgentResponse,
} from "@wakeloop/schemas/types";
import {
  getAuthSessionState,
  type StoredActiveOrganization,
} from "../config/auth-session";
import { resolveBaseUrl } from "../config/base-url";
import { emitCliErrorJson } from "../runtime/cli-error-json";
import { resolveRuntimeContext } from "../runtime/context";
import { createPresenter } from "../runtime/presenter/index";
import { createSilentPresenter } from "../runtime/presenter/silent";
import type { Presenter } from "../runtime/presenter/types";
import type { RuntimeContext } from "../runtime/types";
import { createCliApiClientFoundation } from "../space/api-client-foundation";
import { renderInfoCard } from "../ui/info-card";
import { colorizeAgentLabel } from "../ui/profile-label-color";

export type CenterAgentCommandContext = {
  activeOrganization: StoredActiveOrganization;
  cliJsonOutput: boolean;
  presenter: Presenter;
  runtime: RuntimeContext;
};

type CenterAgentCommandOperation = "audit" | "designate" | "revoke" | "status";

type CenterAgentAuditJournalEntry =
  ListCenterAgentAuditJournalResponse["entries"][number];

type CenterAgentCommandErrorInput = {
  activeOrganization?: StoredActiveOrganization | null;
  agentProfileId?: string;
  code: string;
  details?: Array<{ label: string; value: string }>;
  message: string;
  nextStep: string;
  payload?: Record<string, unknown>;
  presenter: Presenter;
  title: string;
};

type CenterAgentStatusProfile = {
  profileId: string;
  profileName: string;
  agentControllerRef: string | null;
};

const CENTER_AGENT_NO_DESIGNATION_NEXT_STEP =
  "Run `wakeloop center-agent designate <agent-profile-id>` after connecting an Agent Profile you own.";

export async function runCenterAgentStatus(input?: {
  view?: string;
}): Promise<void> {
  const context = await resolveCenterAgentCommandContext(input);
  if (!context) {
    return;
  }

  try {
    const api = await createCenterAgentApiClient();
    const response = await api.getDesignation();
    const profile = response.designation
      ? await loadCenterAgentProfileOrThrow(response.designation.agentProfileId)
      : null;
    emitCenterAgentStatusSuccess({
      activeOrganization: context.activeOrganization,
      presenter: context.presenter,
      profile,
      response,
      runtime: context.runtime,
    });
  } catch (error) {
    emitCenterAgentApiError({
      activeOrganization: context.activeOrganization,
      error,
      operation: "status",
      presenter: context.presenter,
    });
  }
}

export async function runCenterAgentDesignate(input: {
  agentProfileId?: string;
  view?: string;
}): Promise<void> {
  const context = await resolveCenterAgentCommandContext(input);
  if (!context) {
    return;
  }

  const agentProfileId = normalizeAgentProfileId(input.agentProfileId);
  if (!agentProfileId) {
    emitCenterAgentCommandError({
      activeOrganization: context.activeOrganization,
      code: "center_agent.agent_profile_required",
      title: "Agent Profile Required",
      message: "Choose the Agent Profile to designate as the Center Agent.",
      nextStep: "Run `wakeloop center-agent designate <agent-profile-id>`.",
      presenter: context.presenter,
    });
    return;
  }

  try {
    const api = await createCenterAgentApiClient();
    const response = await api.designate({ agentProfileId });
    emitCenterAgentDesignateSuccess({
      activeOrganization: context.activeOrganization,
      presenter: context.presenter,
      response,
      runtime: context.runtime,
    });
  } catch (error) {
    emitCenterAgentApiError({
      activeOrganization: context.activeOrganization,
      agentProfileId,
      error,
      operation: "designate",
      presenter: context.presenter,
    });
  }
}

export async function runCenterAgentRevoke(input?: {
  view?: string;
}): Promise<void> {
  const context = await resolveCenterAgentCommandContext(input);
  if (!context) {
    return;
  }

  try {
    const api = await createCenterAgentApiClient();
    const response = await api.revoke();
    emitCenterAgentRevokeSuccess({
      activeOrganization: context.activeOrganization,
      presenter: context.presenter,
      response,
      runtime: context.runtime,
    });
  } catch (error) {
    emitCenterAgentApiError({
      activeOrganization: context.activeOrganization,
      error,
      operation: "revoke",
      presenter: context.presenter,
    });
  }
}

export async function runCenterAgentAudit(input?: {
  limit?: number;
  view?: string;
}): Promise<void> {
  const context = await resolveCenterAgentCommandContext(input);
  if (!context) {
    return;
  }

  const limit = normalizeAuditLimit(input?.limit);
  if (limit === "invalid") {
    emitCenterAgentCommandError({
      activeOrganization: context.activeOrganization,
      code: "center_agent.audit_limit_invalid",
      title: "Audit Limit Invalid",
      message: "Center Agent audit limit must be between 1 and 200.",
      nextStep: "Run `wakeloop center-agent audit --limit 25`.",
      presenter: context.presenter,
    });
    return;
  }

  try {
    const api = await createCenterAgentApiClient();
    const response = await api.listAuditJournal(
      limit === undefined ? {} : { limit }
    );
    emitCenterAgentAuditSuccess({
      activeOrganization: context.activeOrganization,
      entries: response.entries,
      organizationId: response.organizationId,
      presenter: context.presenter,
      runtime: context.runtime,
    });
  } catch (error) {
    emitCenterAgentApiError({
      activeOrganization: context.activeOrganization,
      error,
      operation: "audit",
      presenter: context.presenter,
    });
  }
}

export async function resolveCenterAgentCommandContext(input?: {
  cliJsonOutput?: boolean;
  view?: string;
}): Promise<CenterAgentCommandContext | null> {
  const runtime = await resolveRuntimeContext({ view: input?.view });
  const cliJsonOutput = input?.cliJsonOutput === true;
  const presenter = cliJsonOutput
    ? createSilentPresenter(runtime.resolvedView)
    : createPresenter(runtime);
  const authState = await getAuthSessionState({
    validateRemote: true,
  });
  if (authState.state !== "valid" || !authState.session) {
    emitCenterAgentContextError({
      activeOrganization: null,
      cliJsonOutput,
      code: "center_agent.auth_required",
      title: "Sign In Needed",
      message: "Sign in to WakeLoop before managing the Center Agent.",
      nextStep: "Run `wakeloop login`, then retry this command.",
      payload: {
        authState: authState.state,
        reasonCode: authState.reasonCode ?? null,
      },
      presenter,
    });
    return null;
  }

  const activeOrganization = authState.session.activeOrganization ?? null;
  if (!activeOrganization) {
    emitCenterAgentContextError({
      activeOrganization: null,
      cliJsonOutput,
      code: "center_agent.organization_required",
      title: "No Active Organization",
      message:
        "Choose an active WakeLoop organization before managing the Center Agent.",
      nextStep: "Run `wakeloop org list`, then `wakeloop org switch <slug>`.",
      presenter,
    });
    return null;
  }

  return {
    activeOrganization,
    cliJsonOutput,
    presenter,
    runtime,
  };
}

function emitCenterAgentContextError(
  input: CenterAgentCommandErrorInput & { cliJsonOutput: boolean }
): void {
  if (!input.cliJsonOutput) {
    emitCenterAgentCommandError(input);
    return;
  }

  process.exitCode = 1;
  emitCliErrorJson({
    code: input.code,
    message: `${input.message} Next: ${input.nextStep}`,
  });
}

async function createCenterAgentApiClient() {
  return createCenterAgentApi(
    createCliApiClientFoundation(await resolveBaseUrl())
  );
}

async function createOrganizationApiClient() {
  return createOrganizationApi(
    createCliApiClientFoundation(await resolveBaseUrl())
  );
}

async function loadCenterAgentProfileOrThrow(
  agentProfileId: string
): Promise<CenterAgentStatusProfile> {
  const api = await createOrganizationApiClient();
  const directory = await api.getCurrentDirectory();
  const profile = directory.agentProfiles.find(
    (candidate) => candidate.profileId === agentProfileId
  );
  if (!profile) {
    const error = new Error(
      "Center Agent designation profile is missing from the Organization directory."
    ) as Error & {
      code?: string;
      status?: number;
    };
    error.code = "center_agent.profile_details_missing";
    error.status = 409;
    throw error;
  }
  return toCenterAgentStatusProfile(profile);
}

function toCenterAgentStatusProfile(
  profile: OrganizationDirectoryAgentProfile
): CenterAgentStatusProfile {
  return {
    agentControllerRef: profile.readiness.agentControllerRef ?? null,
    profileId: profile.profileId,
    profileName: profile.profileName,
  };
}

function emitCenterAgentStatusSuccess(input: {
  activeOrganization: StoredActiveOrganization;
  presenter: Presenter;
  profile: CenterAgentStatusProfile | null;
  response: GetCenterAgentDesignationResponse;
  runtime: RuntimeContext;
}): void {
  const payload = buildCenterAgentStatusPayload({
    activeOrganization: input.activeOrganization,
    profile: input.profile,
    response: input.response,
  });

  if (input.runtime.resolvedView === "human") {
    renderCenterAgentStatusCard({
      activeOrganization: input.activeOrganization,
      designation: input.response.designation,
      presenter: input.presenter,
      profile: input.profile,
    });
    return;
  }

  input.presenter.data({
    code: "center_agent.status",
    payload,
  });
}

function emitCenterAgentDesignateSuccess(input: {
  activeOrganization: StoredActiveOrganization;
  presenter: Presenter;
  response: DesignateCenterAgentResponse;
  runtime: RuntimeContext;
}): void {
  const payload = {
    status: "ok",
    organization: {
      active: input.activeOrganization,
    },
    centerAgent: {
      designation: input.response.designation,
      designationStatus: input.response.status,
      profile: {
        profileId: input.response.designation.agentProfileId,
      },
    },
  };

  if (input.runtime.resolvedView === "human") {
    renderCenterAgentDesignationCard({
      activeOrganization: input.activeOrganization,
      presenter: input.presenter,
      response: input.response,
    });
    return;
  }

  input.presenter.data({
    code: "center_agent.designate",
    payload,
  });
}

function emitCenterAgentRevokeSuccess(input: {
  activeOrganization: StoredActiveOrganization;
  presenter: Presenter;
  response: RevokeCenterAgentResponse;
  runtime: RuntimeContext;
}): void {
  const payload = {
    status: "ok",
    organization: {
      active: input.activeOrganization,
    },
    revocation: input.response,
  };

  if (input.runtime.resolvedView === "human") {
    renderCenterAgentRevocationCard({
      activeOrganization: input.activeOrganization,
      presenter: input.presenter,
      response: input.response,
    });
    return;
  }

  input.presenter.data({
    code: "center_agent.revoke",
    payload,
  });
}

function emitCenterAgentAuditSuccess(input: {
  activeOrganization: StoredActiveOrganization;
  entries: CenterAgentAuditJournalEntry[];
  organizationId: string;
  presenter: Presenter;
  runtime: RuntimeContext;
}): void {
  const payload = {
    status: "ok",
    organization: {
      active: input.activeOrganization,
    },
    organizationId: input.organizationId,
    entries: input.entries,
  };

  if (input.runtime.resolvedView === "human") {
    renderCenterAgentAuditCard({
      activeOrganization: input.activeOrganization,
      entries: input.entries,
      organizationId: input.organizationId,
      presenter: input.presenter,
    });
    return;
  }

  input.presenter.data({
    code: "center_agent.audit",
    payload,
  });
}

function emitCenterAgentApiError(input: {
  activeOrganization: StoredActiveOrganization;
  agentProfileId?: string;
  error: unknown;
  operation: CenterAgentCommandOperation;
  presenter: Presenter;
}): void {
  const failure = toStructuredApiClientFailure(input.error);
  const classified = classifyCenterAgentApiFailure({
    failure,
    operation: input.operation,
  });
  emitCenterAgentCommandError({
    activeOrganization: input.activeOrganization,
    agentProfileId: input.agentProfileId,
    code: classified.code,
    details: buildFailureDetails(failure),
    message: classified.message,
    nextStep: classified.nextStep,
    payload: {
      ...(input.agentProfileId ? { agentProfileId: input.agentProfileId } : {}),
      failure: {
        code: failure.code,
        requestId: failure.requestId,
        status: failure.status,
        upstreamCode: failure.upstreamCode,
      },
    },
    presenter: input.presenter,
    title: classified.title,
  });
}

function classifyCenterAgentApiFailure(input: {
  failure: StructuredApiClientFailure;
  operation: CenterAgentCommandOperation;
}): {
  code: string;
  message: string;
  nextStep: string;
  title: string;
} {
  if (
    input.failure.status === 401 ||
    hasFailureCode(input.failure, "auth.unauthenticated")
  ) {
    return {
      code: "center_agent.auth_required",
      message:
        "WakeLoop could not verify your sign-in for this Center Agent command.",
      nextStep: "Run `wakeloop login`, then retry this command.",
      title: "Sign In Needed",
    };
  }

  if (
    hasFailureCode(
      input.failure,
      "organization.context_required",
      "organization.context_unavailable",
      "organization.membership_required"
    )
  ) {
    return {
      code: "center_agent.organization_required",
      message:
        "WakeLoop could not resolve an active Organization for this Center Agent command.",
      nextStep: "Run `wakeloop org list`, then `wakeloop org switch <slug>`.",
      title: "No Active Organization",
    };
  }

  if (
    hasFailureCode(
      input.failure,
      "center_agent.permission_required",
      "organization.role_required"
    )
  ) {
    return {
      code: "center_agent.owner_or_admin_required",
      message:
        "Only an Organization owner or admin can change the Center Agent designation.",
      nextStep:
        "Run `wakeloop org current` to confirm the active Organization, then ask an owner or admin for access.",
      title: "Owner Or Admin Needed",
    };
  }

  if (hasFailureCode(input.failure, "center_agent.profile_not_found")) {
    return {
      code: "center_agent.profile_not_found",
      message: "WakeLoop could not find that Agent Profile.",
      nextStep: "Check the Agent Profile ID, then retry this command.",
      title: "Agent Profile Not Found",
    };
  }

  if (hasFailureCode(input.failure, "center_agent.profile_invalid")) {
    return {
      code: "center_agent.profile_invalid",
      message: "Only an Agent Profile can be designated as the Center Agent.",
      nextStep: "Choose an Agent Profile ID, then retry this command.",
      title: "Agent Profile Required",
    };
  }

  if (hasFailureCode(input.failure, "center_agent.profile_org_mismatch")) {
    return {
      code: "center_agent.profile_org_mismatch",
      message: "That Agent Profile does not belong to the active Organization.",
      nextStep:
        "Run `wakeloop org current` and choose an Agent Profile from that Organization.",
      title: "Agent Profile Not In Organization",
    };
  }

  if (hasFailureCode(input.failure, "center_agent.profile_binding_required")) {
    return {
      code: "center_agent.profile_binding_required",
      message:
        "Designating a self-hosted Center Agent requires your enabled primary Binding for that Agent Profile.",
      nextStep:
        "Run `wakeloop setup --profile <agent-profile-id> --local-agent <local-agent-id>`, then retry designation.",
      title: "Agent Profile Binding Needed",
    };
  }

  if (hasFailureCode(input.failure, "center_agent.profile_details_missing")) {
    return {
      code: "center_agent.profile_details_missing",
      message:
        "The Center Agent designation exists, but its Agent Profile is not visible in the Organization directory.",
      nextStep: "Retry after WakeLoop refreshes the Organization directory.",
      title: "Center Agent Profile Missing",
    };
  }

  if (input.failure.code === "gateway.invalid_response") {
    return {
      code: "center_agent.response_invalid",
      message: "WakeLoop returned an invalid Center Agent response.",
      nextStep: "Retry after WakeLoop Gateway is updated and healthy.",
      title: "Center Agent Response Invalid",
    };
  }

  if (
    hasFailureCode(
      input.failure,
      "center_agent.audit_store_unavailable",
      "center_agent.designation_store_unavailable"
    )
  ) {
    return {
      code: "center_agent.store_unavailable",
      message:
        "WakeLoop could not reach the Center Agent designation or audit store.",
      nextStep: "Retry after WakeLoop is healthy.",
      title: "Center Agent Store Unavailable",
    };
  }

  const operationLabel = formatCenterAgentOperationLabel(input.operation);
  return {
    code: `center_agent.${input.operation}_failed`,
    message: `WakeLoop could not ${operationLabel}.`,
    nextStep: "Retry after WakeLoop Gateway is reachable.",
    title: "Center Agent Command Failed",
  };
}

function emitCenterAgentCommandError(
  input: CenterAgentCommandErrorInput
): void {
  process.exitCode = 1;
  const payload = {
    status: "error",
    organization: {
      active: input.activeOrganization ?? null,
    },
    error: {
      code: input.code,
      message: input.message,
      nextStep: input.nextStep,
    },
    ...(input.agentProfileId ? { agentProfileId: input.agentProfileId } : {}),
    ...(input.payload ?? {}),
  };

  if (input.presenter.view === "human") {
    renderInfoCard({
      codePrefix: "center-agent.error.card",
      minContentWidth: 72,
      presenter: input.presenter,
      rows: [
        { label: "Reason", value: input.message },
        { label: "Next Step", value: input.nextStep },
        ...(input.details ?? []),
      ],
      sanitize: true,
      title: input.title,
    });
    return;
  }

  input.presenter.data({
    code: input.code,
    payload,
  });
}

function renderCenterAgentStatusCard(input: {
  activeOrganization: StoredActiveOrganization;
  designation: CenterAgentDesignation | null;
  presenter: Presenter;
  profile: CenterAgentStatusProfile | null;
}): void {
  if (!input.designation) {
    renderInfoCard({
      codePrefix: "center-agent.status.card",
      minContentWidth: 72,
      presenter: input.presenter,
      rows: [
        {
          label: "Organization",
          value: formatOrganizationLabel(input.activeOrganization),
        },
        { label: "Status", value: "No Center Agent is designated." },
        { label: "Next Step", value: CENTER_AGENT_NO_DESIGNATION_NEXT_STEP },
      ],
      sanitize: true,
      title: "No Center Agent Designated",
    });
    return;
  }

  renderInfoCard({
    codePrefix: "center-agent.status.card",
    minContentWidth: 72,
    presenter: input.presenter,
    rows: [
      {
        label: "Organization",
        value: formatOrganizationLabel(input.activeOrganization),
      },
      {
        label: "Center Agent",
        value: input.profile
          ? formatCenterAgentProfileLabel(input.profile)
          : input.designation.agentProfileId,
      },
      { label: "Agent Profile ID", value: input.designation.agentProfileId },
      { label: "Designated By", value: input.designation.designatedBy },
      { label: "Created At", value: input.designation.createdAt },
    ],
    sanitize: true,
    title: "Center Agent",
  });
}

function renderCenterAgentDesignationCard(input: {
  activeOrganization: StoredActiveOrganization;
  presenter: Presenter;
  response: DesignateCenterAgentResponse;
}): void {
  renderInfoCard({
    codePrefix: "center-agent.designate.card",
    minContentWidth: 72,
    presenter: input.presenter,
    rows: [
      {
        label: "Organization",
        value: formatOrganizationLabel(input.activeOrganization),
      },
      {
        label: "Agent Profile ID",
        value: input.response.designation.agentProfileId,
      },
      { label: "Status", value: input.response.status },
      {
        label: "Result",
        value: `This Agent Profile is now the Center Agent for ${input.activeOrganization.name}.`,
      },
      {
        label: "Designated By",
        value: input.response.designation.designatedBy,
      },
      { label: "Created At", value: input.response.designation.createdAt },
    ],
    sanitize: true,
    title: resolveDesignationTitle(input.response.status),
  });
}

function renderCenterAgentRevocationCard(input: {
  activeOrganization: StoredActiveOrganization;
  presenter: Presenter;
  response: RevokeCenterAgentResponse;
}): void {
  renderInfoCard({
    codePrefix: "center-agent.revoke.card",
    minContentWidth: 72,
    presenter: input.presenter,
    rows: [
      {
        label: "Organization",
        value: formatOrganizationLabel(input.activeOrganization),
      },
      { label: "Revoked", value: input.response.revoked ? "yes" : "no" },
      ...(input.response.revokedAgentProfileId
        ? [
            {
              label: "Revoked Agent Profile ID",
              value: input.response.revokedAgentProfileId,
            },
          ]
        : [{ label: "Status", value: "No Center Agent was designated." }]),
    ],
    sanitize: true,
    title: input.response.revoked
      ? "Center Agent Revoked"
      : "No Center Agent To Revoke",
  });
}

function renderCenterAgentAuditCard(input: {
  activeOrganization: StoredActiveOrganization;
  entries: CenterAgentAuditJournalEntry[];
  organizationId: string;
  presenter: Presenter;
}): void {
  renderInfoCard({
    codePrefix: "center-agent.audit.card",
    minContentWidth: 72,
    presenter: input.presenter,
    rows: [
      {
        label: "Organization",
        value: formatOrganizationLabel(input.activeOrganization),
      },
      { label: "Organization ID", value: input.organizationId },
      { label: "Audit Entries", value: String(input.entries.length) },
      ...input.entries.map((entry) => ({
        label: "Entry",
        value: formatAuditEntry(entry),
      })),
    ],
    sanitize: true,
    title: "Center Agent Audit",
  });
}

function buildCenterAgentStatusPayload(input: {
  activeOrganization: StoredActiveOrganization;
  profile: CenterAgentStatusProfile | null;
  response: GetCenterAgentDesignationResponse;
}) {
  return {
    status: "ok",
    organization: {
      active: input.activeOrganization,
    },
    centerAgent: {
      designated: input.response.designation !== null,
      designation: input.response.designation,
      profile: input.profile
        ? {
            profileId: input.profile.profileId,
            profileName: input.profile.profileName,
          }
        : null,
      ...(input.response.designation
        ? {}
        : { nextStep: CENTER_AGENT_NO_DESIGNATION_NEXT_STEP }),
    },
  };
}

function buildFailureDetails(
  failure: StructuredApiClientFailure
): Array<{ label: string; value: string }> {
  return [
    ...(failure.status === null
      ? []
      : [{ label: "HTTP Status", value: String(failure.status) }]),
    ...(failure.requestId
      ? [{ label: "Request ID", value: failure.requestId }]
      : []),
    ...(failure.upstreamCode
      ? [{ label: "Upstream Code", value: failure.upstreamCode }]
      : []),
    ...(failure.schemaIssueMessage
      ? [{ label: "Schema Issue", value: failure.schemaIssueMessage }]
      : []),
  ];
}

function hasFailureCode(
  failure: StructuredApiClientFailure,
  ...codes: string[]
): boolean {
  return codes.some(
    (code) => failure.code === code || failure.upstreamCode === code
  );
}

function formatCenterAgentOperationLabel(
  operation: CenterAgentCommandOperation
): string {
  if (operation === "audit") {
    return "list Center Agent audit entries";
  }
  if (operation === "designate") {
    return "designate this Center Agent";
  }
  if (operation === "revoke") {
    return "revoke the Center Agent designation";
  }
  return "read the Center Agent designation";
}

function formatCenterAgentProfileLabel(
  profile: CenterAgentStatusProfile
): string {
  const label = colorizeAgentLabel(profile.profileName, {
    ...(profile.agentControllerRef
      ? { agentControllerRef: profile.agentControllerRef }
      : {}),
    profileId: profile.profileId,
    profileName: profile.profileName,
  });
  return `${label} (${profile.profileId})`;
}

function formatAuditEntry(entry: CenterAgentAuditJournalEntry): string {
  return [
    formatCenterAgentAction(entry.action),
    `actor ${entry.actorProfileId}`,
    `on behalf of ${formatOnBehalfOf(entry.onBehalfOf)}`,
    entry.createdAt,
  ].join(" | ");
}

function formatOnBehalfOf(
  onBehalfOf: CenterAgentAuditJournalEntry["onBehalfOf"]
): string {
  if (!onBehalfOf) {
    return "none";
  }
  return `${onBehalfOf.displayName} (${onBehalfOf.profileId})`;
}

function formatCenterAgentAction(action: string): string {
  return action.replaceAll("_", " ");
}

function resolveDesignationTitle(
  status: DesignateCenterAgentResponse["status"]
): string {
  if (status === "replaced") {
    return "Center Agent Replaced";
  }
  if (status === "unchanged") {
    return "Center Agent Unchanged";
  }
  return "Center Agent Designated";
}

function formatOrganizationLabel(
  organization: StoredActiveOrganization
): string {
  const role = organization.role ? `, ${organization.role}` : "";
  return `${organization.name} (${organization.slug})${role}`;
}

function normalizeAgentProfileId(value: unknown): string | null {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeAuditLimit(
  value: number | undefined
): number | undefined | "invalid" {
  if (value === undefined) {
    return undefined;
  }
  if (!Number.isInteger(value) || value < 1 || value > 200) {
    return "invalid";
  }
  return value;
}
